import { Component, Input, Output, EventEmitter, inject, OnInit, OnChanges, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Project, ProjectStatus } from '../../../../shared/models/project.model';
import { ProjectService } from '../../../../shared/services/project.service';
import { ConfirmModalComponent } from '../../../../shared/utils/modal-validation/modal-validation';
import { TaskService } from '../../../../shared/services/task.service';
import { ProjectStage } from '../../../../shared/models/project.model';
import { Task } from '../../../../shared/models/task.model';
import { User as UserModel } from '../../../../shared/models/user.model';
import { ManageProjectManagersComponent } from '../../../../admin/employees/manage-project-managers/manage-project-managers';
import { ManageProjectMembersComponent } from '../../../../admin/employees/manage-project-members/manage-project-members';
import { User as UserAuthService } from '../../../../shared/services/user';
import { UserService } from '../../../../shared/services/user.service';
import { TimeEntriesService } from '../../../../shared/services/time-entries.service';

@Component({
  selector: 'app-project-list',
  imports: [CommonModule, ConfirmModalComponent, ManageProjectManagersComponent, ManageProjectMembersComponent],
  templateUrl: './list-projects.html',
  styleUrls: ['./list-projects.scss']
})
export class ListProjectsComponent implements OnInit, OnChanges {
  private projectService = inject(ProjectService);
  userAuthService = inject(UserAuthService);  // For admin check
  private userService = inject(UserService);   // For users list
  private taskService = inject(TaskService);
  private timeEntriesService = inject(TimeEntriesService);

  @Input() projects: Project[] = [];
  @Output() projectClick = new EventEmitter<string>();

  showConfirm = false;
  projectId?: string;
  confirmMessage = '';
  openMenuId?: string;

  // Manage managers modal
  showManageManagers = false;
  // Manage members modal
  showManageMembers = false;
  selectedProjectId?: string;
  selectedProjectName?: string;

  projectStages: ProjectStage[] = [];
  tasks: Task[] = [];
  employees: UserModel[] = [];

  /** 🔥 projectId -> employees */
  employeesByProject: Record<string, UserModel[]> = {};

  /** projectId -> real encoded hours from time entries */
  encodedHoursByProject: Record<string, number> = {};
  private _loadedProjectIds = new Set<string>();



  // Close menu when clicking outside
  @HostListener('document:click')
  onDocumentClick() {
    this.openMenuId = undefined;
  }

  toggleMenu(projectId: string) {
    event?.stopPropagation();
    this.openMenuId = this.openMenuId === projectId ? undefined : projectId;
  }

  ngOnInit(): void {
    this.projectService.projectsStage$.subscribe(stages => {
      this.projectStages = stages;
      this.rebuildEmployeesMap();
    });

    this.taskService.tasks$.subscribe(tasks => {
      this.tasks = tasks;
      this.rebuildEmployeesMap();
    });

    this.userService.users$.subscribe(emps => {
      this.employees = emps;
      this.rebuildEmployeesMap();
    });

    // Load encoded hours for all projects
    this.loadAllEncodedHours();
  }

  ngOnChanges() {
    // When projects input changes, load encoded hours for any new projects
    this.loadAllEncodedHours();
  }

  /** Max concurrent time-entry fetches — high enough to load fast, capped to avoid throttling AppSync. */
  private static readonly HOURS_FETCH_CONCURRENCY = 8;

  private async loadAllEncodedHours() {
    // Only fetch projects we haven't loaded yet; reserve them up-front so concurrent
    // ngOnChanges/ngOnInit calls don't double-fetch the same project.
    const toLoad = this.projects.filter(
      p => p.projectId && !this._loadedProjectIds.has(p.projectId)
    );
    if (!toLoad.length) return;
    toLoad.forEach(p => this._loadedProjectIds.add(p.projectId!));

    // Fetch in parallel with a bounded concurrency instead of one-at-a-time.
    const queue = [...toLoad];
    const worker = async () => {
      let project: Project | undefined;
      while ((project = queue.shift())) {
        const id = project.projectId!;
        try {
          const entries = await this.timeEntriesService.fetchAllByProjectId(id, project.projectName);
          this.encodedHoursByProject[id] = entries.reduce((acc, e) => {
            const ms = new Date(e.endTime).getTime() - new Date(e.startTime).getTime();
            return acc + ms / 3_600_000;
          }, 0);
        } catch (e) {
          console.error(`[ListProjects] Failed to load hours for ${id}:`, e);
          this.encodedHoursByProject[id] = 0;
        }
      }
    };

    const workers = Array.from(
      { length: Math.min(ListProjectsComponent.HOURS_FETCH_CONCURRENCY, toLoad.length) },
      () => worker()
    );
    await Promise.all(workers);
  }

  getEncodedHours(projectId: string | undefined): number {
    return projectId ? (this.encodedHoursByProject[projectId] ?? 0) : 0;
  }

  getBudgetPercentage(project: Project): number {
    const encoded = this.getEncodedHours(project.projectId);
    const budget = project.estimatedHours || 0;
    if (budget <= 0) return 0;
    return Math.min((encoded / budget) * 100, 100);
  }

  isOverBudget(project: Project): boolean {
    const encoded = this.getEncodedHours(project.projectId);
    const budget = project.estimatedHours || 0;
    return budget > 0 && encoded > budget;
  }

  /** Vivid dot / progress color, deterministic per project. */
  private static readonly DOT_COLORS = ['#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b', '#ec4899', '#06b6d4', '#ef4444', '#0ea5e9'];
  dotColor(key: string): string {
    const s = key ?? '';
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return ListProjectsComponent.DOT_COLORS[h % ListProjectsComponent.DOT_COLORS.length];
  }

  /** Project deadline = latest phase end-date (projects have no deadline field). */
  getDeadline(project: Project): string | null {
    const stages = this.projectStages.filter(s => s.projectId === project.projectId && s.endDate);
    if (!stages.length) return null;
    return stages.reduce((max, s) => (s.endDate > max ? s.endDate : max), stages[0].endDate);
  }

  /**
   * "Done" for display purposes: an explicit Done/Archived status, or — for a
   * project nobody has set a status on — every phase marked DONE.
   */
  isProjectDone(project: Project): boolean {
    if (project.status === 'DONE' || project.status === 'ARCHIVED') return true;
    if (project.status === 'STANDBY') return false;
    const stages = this.projectStages.filter(s => s.projectId === project.projectId);
    return stages.length > 0 && stages.every(s => s.status === 'DONE');
  }

  private daysUntil(dateStr: string): number {
    const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
    const now = new Date(); now.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - now.getTime()) / 86_400_000);
  }

  /** Derived status pill: done / soon (deadline ≤ 7d) / active. */
  projectStatus(project: Project): 'done' | 'soon' | 'active' {
    if (this.isProjectDone(project)) return 'done';
    const dl = this.getDeadline(project);
    if (dl != null && this.daysUntil(dl) <= 7) return 'soon';
    return 'active';
  }

  statusMeta(status: 'done' | 'soon' | 'active'): { label: string; cls: string } {
    switch (status) {
      case 'done': return { label: 'Done', cls: 'bg-gray-100 text-gray-500 border-gray-200' };
      case 'soon': return { label: 'Soon', cls: 'bg-amber-50 text-amber-700 border-amber-100' };
      default: return { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' };
    }
  }

  /**
   * The pill shown in the Status column. An explicit status wins; a project that
   * has never been given one keeps the old derived pill, which still carries the
   * "deadline is close" warning that a plain "Active" would lose.
   */
  statusPill(project: Project): { label: string; cls: string } {
    switch (project.status) {
      case 'ARCHIVED': return { label: 'Archived', cls: 'bg-gray-100 text-gray-400 border-gray-200' };
      case 'STANDBY': return { label: 'Stand-by', cls: 'bg-orange-50 text-orange-700 border-orange-100' };
      case 'DONE': return { label: 'Done', cls: 'bg-gray-100 text-gray-500 border-gray-200' };
      default: return this.statusMeta(this.projectStatus(project));
    }
  }

  /** The options offered in the overflow menu, current status excluded. */
  readonly statusChoices: { value: ProjectStatus; label: string }[] = [
    { value: 'ACTIVE', label: 'Mark as active' },
    { value: 'STANDBY', label: 'Put on stand-by' },
    { value: 'DONE', label: 'Mark as done' },
    { value: 'ARCHIVED', label: 'Archive' },
  ];

  isCurrentStatus(project: Project, status: ProjectStatus): boolean {
    return (project.status ?? 'ACTIVE') === status;
  }

  async setStatus(project: Project, status: ProjectStatus, event: Event) {
    event.stopPropagation();
    this.openMenuId = undefined;
    if (!project.projectId || this.isCurrentStatus(project, status)) return;
    // Optimistic: the row re-pills at once, the store refresh confirms it.
    project.status = status;
    await this.projectService.setProjectStatus(project.projectId, status);
  }

  /** Color for the deadline text: overdue (red) / soon ≤3d (amber) / normal. */
  deadlineClass(project: Project): string {
    if (this.isProjectDone(project)) return 'text-gray-300';
    const dl = this.getDeadline(project);
    if (dl == null) return 'text-gray-300';
    const d = this.daysUntil(dl);
    if (d < 0) return 'text-red-600 font-medium';
    if (d <= 3) return 'text-amber-600 font-medium';
    return 'text-gray-500';
  }

  /** 🔥 LOGIQUE CENTRALE */
  private rebuildEmployeesMap() {
    if (!this.projects.length || !this.employees.length) {
      this.employeesByProject = {};
      return;
    }

    const map: Record<string, UserModel[]> = {};

    // For each project, get all members (managers + members)
    for (const project of this.projects) {
      if (!project.projectId) continue;
      map[project.projectId] = this.projectService.getProjectMemberUsers(project, this.employees);
    }

    this.employeesByProject = map;
  }

  /**
   * Returns a deduplicated, fused list of all people associated with a project:
   * - project.managers (via user embed)
   * - project.members (via user embed)
   * - task-assigned employees (via task.employeeId on project stages)
   */
  getFusedTeam(project: Project): { id: string; firstName: string; lastName: string; role: 'manager' | 'member' | 'assignee' }[] {
    const seen = new Set<string>();
    const result: { id: string; firstName: string; lastName: string; role: 'manager' | 'member' | 'assignee' }[] = [];

    // 1. Managers (embedded user data; archived people are dropped from the team)
    for (const m of project.managers ?? []) {
      const id = m.userId;
      if (!id || seen.has(id) || m.user?.isArchived) continue;
      seen.add(id);
      result.push({
        id,
        firstName: m.user?.firstName ?? '',
        lastName: m.user?.name ?? '',
        role: 'manager'
      });
    }

    // 2. Members (embedded user data)
    for (const m of project.members ?? []) {
      const id = m.userId;
      if (!id || seen.has(id) || m.user?.isArchived) continue;
      seen.add(id);
      result.push({
        id,
        firstName: m.user?.firstName ?? '',
        lastName: m.user?.name ?? '',
        role: 'member'
      });
    }

    // 3. Task-assigned employees: filter stages to this project, then match task.employeeId
    const projectStages = this.projectStages.filter(s => s.projectId === project.projectId);
    const taskAssignees = this.projectService.getEmployeesForProjectTasks(projectStages, this.tasks, this.employees);
    for (const emp of taskAssignees) {
      if (!emp.id || seen.has(emp.id) || emp.isArchived) continue;
      seen.add(emp.id);
      result.push({
        id: emp.id,
        firstName: emp.firstName ?? '',
        lastName: emp.name ?? '',
        role: 'assignee'
      });
    }

    return result;
  }


  /** Two-letter (or one-letter) initials for an avatar, uppercased. */
  initials(firstName?: string, lastName?: string): string {
    const f = (firstName ?? '').trim();
    const l = (lastName ?? '').trim();
    const result = `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
    return result || '?';
  }

  /** Soft monogram palette (background + foreground), picked deterministically per project name. */
  private static readonly MONOGRAM_PALETTE = [
    { bg: '#EEF2FF', fg: '#4338CA' }, // indigo
    { bg: '#ECFEFF', fg: '#0E7490' }, // cyan
    { bg: '#ECFDF5', fg: '#047857' }, // emerald
    { bg: '#FEF3C7', fg: '#B45309' }, // amber
    { bg: '#FCE7F3', fg: '#BE185D' }, // pink
    { bg: '#F3E8FF', fg: '#7E22CE' }, // purple
    { bg: '#FFE4E6', fg: '#BE123C' }, // rose
    { bg: '#E0F2FE', fg: '#0369A1' }, // sky
  ];

  /** A colored monogram (initials + deterministic color) for a project, so rows are easy to tell apart. */
  monogram(name: string): { bg: string; fg: string; text: string } {
    const clean = (name ?? '').trim();
    const words = clean.split(/\s+/).filter(Boolean);
    const text = (
      words.length >= 2 ? words[0].charAt(0) + words[1].charAt(0) : clean.slice(0, 2)
    ).toUpperCase() || '?';
    let hash = 0;
    for (let i = 0; i < clean.length; i++) hash = (hash * 31 + clean.charCodeAt(i)) >>> 0;
    const palette = ListProjectsComponent.MONOGRAM_PALETTE;
    return { ...palette[hash % palette.length], text };
  }

  /**
   * Budget health for the Time cell:
   * - 'empty' : no estimate, or nothing logged yet → neutral
   * - 'ok'    : under 80% of the estimate
   * - 'warn'  : 80–100% of the estimate
   * - 'over'  : above the estimate
   */
  budgetTier(project: Project): 'empty' | 'ok' | 'warn' | 'over' {
    const estimate = project.estimatedHours || 0;
    if (estimate <= 0) return 'empty';
    const encoded = this.getEncodedHours(project.projectId);
    if (encoded <= 0) return 'empty';
    const pct = (encoded / estimate) * 100;
    if (pct > 100) return 'over';
    if (pct >= 80) return 'warn';
    return 'ok';
  }

  onProjectClick(projectId?: string) {
    if (projectId) this.projectClick.emit(projectId);
  }

  deleteProject(projectId?: string) {
    this.openMenuId = undefined;
    if (!projectId) return;
    this.projectId = projectId;
    this.confirmMessage = 'Are you sure you want to delete this project and all its phases?';
    this.showConfirm = true;
  }

  openManageManagers(projectId?: string, projectName?: string) {
    this.openMenuId = undefined;
    if (!projectId) return;
    this.selectedProjectId = projectId;
    this.selectedProjectName = projectName;
    this.showManageManagers = true;
  }

  closeManageManagers() {
    this.showManageManagers = false;
    this.selectedProjectId = undefined;
    this.selectedProjectName = undefined;
  }

  /**
   * Team avatars are the shortcut for assigning members. Non-admins keep the
   * plain row behaviour (the click falls through and opens the project).
   */
  onTeamClick(project: Project, event: Event) {
    if (!this.userAuthService.admin()) return;
    event.stopPropagation();
    this.openManageMembers(project.projectId, project.projectName);
  }

  openManageMembers(projectId?: string, projectName?: string) {
    this.openMenuId = undefined;
    if (!projectId) return;
    this.selectedProjectId = projectId;
    this.selectedProjectName = projectName;
    this.showManageMembers = true;
  }

  closeManageMembers() {
    this.showManageMembers = false;
    this.selectedProjectId = undefined;
    this.selectedProjectName = undefined;
  }

  handleConfirm(result: 'ok' | 'cancel') {
    this.showConfirm = false;
    if (result === 'ok' && this.projectId) {
      this.projectService.deleteProject(this.projectId)
        .catch(err => console.error(err));
    }
  }
}
